import React, { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  makeStyles,
  Portal,
  Theme,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { DraggableControlContext } from './RequestCommonLayout';
import useQueryString from '@hooks/useQueryString';

interface PannelLayoutProps {
  content: React.ReactNode;
  bottomNav?: React.ReactNode;
}

interface PannelLayoutStyleProps {
  hasBottomNav: boolean;
  hasScroll: boolean;
}

const PannelLayout: React.FC<PannelLayoutProps> = ({ content, bottomNav }) => {
  const { query } = useQueryString<{ stage: string }>();
  const isPreview = query.stage === 'PREVIEW';
  const { initializeStyle } = useContext(DraggableControlContext);
  const [hasScroll, setHasScroll] = useState<boolean>(false);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const contentDiv = useRef<HTMLDivElement | null>(null);
  const { container, contentBox, bottomNavBox } = useStyles({
    hasBottomNav: Boolean(bottomNav),
    hasScroll
  });

  useEffect(() => {
    if (contentDiv.current && window) {
      const needToScroll = matches
        ? window.innerHeight - 83 < contentDiv.current.clientHeight - 20
        : window.innerHeight - 83 < contentDiv.current.clientHeight;

      setHasScroll(needToScroll);
    }
  }, [content, matches]);

  useEffect(() => {
    if (initializeStyle) {
      initializeStyle();
    }
  }, [content, initializeStyle]);

  return (
    <div className={clsx([container, 'draggable-conatiner'])}>
      <div
        className={clsx([
          contentBox,
          'draggable-content',
          {
            preview: isPreview
          }
        ])}
        ref={contentDiv}
      >
        <div className="paperTop" />
        {content}
      </div>
      {bottomNav && !matches && (
        <div className={clsx([bottomNavBox, 'draggable-bottomNav'])}>
          {bottomNav}
        </div>
      )}
      {bottomNav && matches && (
        <Portal>
          <div className={clsx([bottomNavBox, 'draggable-bottomNav'])}>
            {bottomNav}
          </div>
        </Portal>
      )}
    </div>
  );
};

export default PannelLayout;

const useStyles = makeStyles<Theme, PannelLayoutStyleProps>((theme) => ({
  container: {
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    '& form': {
      width: '100%'
    }
  },
  contentBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'visible',
    minHeight: 'calc(100vh - 83px)',
    maxWidth: '464px',
    width: 'calc(100% - 32px)',
    '&.preview': {
      transform: 'translateY(-1px)',
      maxWidth: '100%',
      width: '100%'
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 'calc(100vh - 83px - 160px)',
      justifyContent: 'flex-start',
      paddingTop: 20,
      maxWidth: 'unset',
      width: 'calc(100% - 32px)',
      paddingBottom: '83px',
      overflow: ({ hasScroll }) => (!hasScroll ? 'auto' : 'hidden'),
      '&.preview': {
        maxWidth: 'unset',
        width: '100%',
        paddingTop: '0'
      }
    },
    [theme.breakpoints.up('md')]: {
      padding: () => '100px 0px'
    }
  },
  bottomNavBox: {
    position: 'sticky',
    width: '100%',
    height: '82px',
    bottom: 0,
    [theme.breakpoints.down('sm')]: {
      position: 'fixed'
    }
  }
}));
